import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import BOLayout from '@/pages/back-office/BOLayout';
import api from '@/services/api';
import jsPDF from 'jspdf';

const BOBarcodes = () => {
  const params = useParams();
  const location = useLocation();
  const [restaurantId, setRestaurantId] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [productsStats, setProductsStats] = useState({ total: 0, withBarcode: 0, withoutBarcode: 0 });
  const [selectedProducts, setSelectedProducts] = useState({});
  const [productsLoaded, setProductsLoaded] = useState(false);
  
  // Pagination et optimisation
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [showAllBarcodes, setShowAllBarcodes] = useState(false);
  
  // Filtrage par catégorie
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // Option d'impression
  const [printFormat, setPrintFormat] = useState('brother-ql700'); // 'standard' ou 'brother-ql700'
  
  // Initialiser l'ID du restaurant
  useEffect(() => {
    // Récupérer l'ID du restaurant depuis l'URL (format /back-office/restaurants/:restaurantId/barcodes)
    const pathSegments = location.pathname.split('/');
    const restaurantIdIndex = pathSegments.findIndex(segment => segment === 'restaurants') + 1;
    
    if (pathSegments[restaurantIdIndex]) {
      setRestaurantId(pathSegments[restaurantIdIndex]);
      console.log("Restaurant ID from URL:", pathSegments[restaurantIdIndex]);
    } else if (params.restaurantId) {
      setRestaurantId(params.restaurantId);
      console.log("Restaurant ID from params:", params.restaurantId);
    }
  }, [location, params]);

  // Charger les catégories une fois le restaurant défini
  useEffect(() => {
    if (restaurantId) {
      loadCategories();
    }
  }, [restaurantId]);

  // Charger automatiquement les produits au chargement de la page (optimisé)
  useEffect(() => {
    if (restaurantId) {
      loadProducts();
    }
  }, [restaurantId, page, limit, selectedCategory]);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await api.get(`/barcodes/restaurants/${restaurantId}/categories`);
      if (response.data.success) {
        setCategories(response.data.categories || []);
      }
    } catch (err) {
      console.error("Erreur lors du chargement des catégories:", err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadProducts = async () => {
    try {
      console.log("Loading products for restaurant:", restaurantId);
      setLoading(true);
      setError(null);
      
      // Utiliser la pagination pour réduire la taille des données
      const params = { page, limit, metadataOnly: true };
      if (selectedCategory) {
        params.category = selectedCategory;
      }
      
      const response = await api.get(`/barcodes/restaurants/${restaurantId}/products`, {
        params
      });
      
      console.log("Products response:", response.data);
      
      const productsList = response.data.products || [];
      setProducts(productsList);
      
      // Calculer les statistiques
      const withBarcode = response.data.stats?.withBarcode || productsList.filter(p => p.hasBarcode).length;
      const total = response.data.stats?.total || productsList.length;
      const withoutBarcode = response.data.stats?.withoutBarcode || (total - withBarcode);
      
      setProductsStats({
        total,
        withBarcode,
        withoutBarcode
      });
      
      setTotalPages(response.data.pagination?.totalPages || 1);
      setProductsLoaded(true);
    } catch (err) {
      console.error("Detailed error:", err.response || err);
      setError("Erreur lors du chargement des produits");
    } finally {
      setLoading(false);
    }
  };

  // Charger l'image du code-barres d'un produit spécifique
  const loadBarcodeImage = async (productId) => {
    if (expandedProduct === productId) {
      setExpandedProduct(null);
      return;
    }
    
    try {
      const response = await api.get(`/barcodes/product/${productId}/barcode`);
      
      // Mettre à jour l'URL du code-barres pour ce produit uniquement
      setProducts(prevProducts => prevProducts.map(p => 
        p.productId === productId 
          ? { ...p, barcodeUrl: response.data.barcodeUrl }
          : p
      ));
      
      setExpandedProduct(productId);
    } catch (err) {
      console.error("Erreur lors du chargement du code-barres:", err);
    }
  };

  // Charger tous les codes-barres d'un coup
  const loadAllBarcodes = async () => {
    setShowAllBarcodes(true);
    
    const productsToLoad = products.filter(p => p.hasBarcode && !p.barcodeUrl);
    
    for (const product of productsToLoad) {
      try {
        const response = await api.get(`/barcodes/product/${product.productId}/barcode`);
        
        setProducts(prevProducts => prevProducts.map(p => 
          p.productId === product.productId 
            ? { ...p, barcodeUrl: response.data.barcodeUrl }
            : p
        ));
      } catch (err) {
        console.error(`Erreur lors du chargement du code-barres pour ${product.name}:`, err);
      }
    }
  };
  
  // Masquer tous les codes-barres
  const hideAllBarcodes = () => {
    setShowAllBarcodes(false);
    setExpandedProduct(null);
  };

  const generateBarcodes = async (onlyMissing = false) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const endpoint = onlyMissing 
        ? `/barcodes/restaurants/${restaurantId}/generate-missing` 
        : `/barcodes/restaurants/${restaurantId}/generate`;
        
      const response = await api.post(endpoint);
      const productsList = response.data.products || [];
      setProducts(productsList);
      
      // Mettre à jour les statistiques
      const withBarcode = productsList.filter(p => p.barcodeUrl).length;
      setProductsStats({
        total: productsList.length,
        withBarcode: withBarcode,
        withoutBarcode: productsList.length - withBarcode
      });
      
      setSuccess(onlyMissing 
        ? "Codes-barres manquants générés avec succès" 
        : "Codes-barres générés avec succès");
      
      setProductsLoaded(true);
    } catch (err) {
      setError("Erreur lors de la génération des codes-barres");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const selectAllProducts = () => {
    const newSelection = {};
    products.forEach(product => {
      if (product.hasBarcode) {
        newSelection[product.productId] = true;
      }
    });
    setSelectedProducts(newSelection);
  };

  const deselectAllProducts = () => {
    setSelectedProducts({});
  };

  const printBarcodes = async () => {
    try {
      setGeneratingPDF(true);
      setError(null);
      
      // Filtrer les produits sélectionnés avec code-barres
      const productsWithBarcodes = products.filter(p => 
        p.barcodeUrl && selectedProducts[p.productId]
      );
      
      if (productsWithBarcodes.length === 0) {
        setError("Aucun code-barres n'a été sélectionné");
        setGeneratingPDF(false);
        return;
      }

      // Récupérer les données des produits avec codes-barres du serveur
      const response = await api.post('/barcodes/print', {
        products: productsWithBarcodes,
        quantity: selectedQuantity
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Erreur lors de la préparation des données");
      }

      // Générer le PDF selon le format sélectionné
      if (printFormat === 'brother-ql700') {
        await generateBrotherQL700PDF(response.data.data.products);
        setSuccess("PDF généré avec succès (format Brother QL 700 - 29x90mm)");
      } else {
      await generatePDF(response.data.data.products);
        setSuccess("PDF généré avec succès (format standard)");
      }
    } catch (err) {
      setError("Erreur lors de la génération du PDF");
      console.error(err);
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Fonction pour générer le PDF côté client
  const generatePDF = async (products) => {
    return new Promise(async (resolve, reject) => {
      try {
        // Accéder à jsPDF via la variable globale
       // const { jsPDF } = window.jspdf;
        
        // Dimensions du PDF
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        
        // Dimensions pour les codes-barres sur la page
        const barcodeWidth = 50; // Largeur en mm
        const barcodeHeight = 20; // Hauteur en mm
        const margin = 10; // Marge en mm
        const columns = 3; // Nombre de codes-barres par ligne
        const rows = 10; // Nombre de lignes par page
        
        let x = margin;
        let y = margin;
        let pageIndex = 1;
        
        // Ajout de la pagination en pied de page
        const addPagination = (pageNumber) => {
          doc.setFontSize(8);
          doc.text(`Page ${pageNumber}`, pageWidth / 2, pageHeight - 5, { align: "center" });
        };
        
        addPagination(pageIndex);
        
        // Fonction pour charger une image depuis une URL en utilisant le proxy
        const loadImage = (originalUrl) => {
          return new Promise((resolve, reject) => {
            // Utiliser notre proxy backend pour éviter les problèmes CORS
            const proxyUrl = `${api.defaults.baseURL}/barcodes/proxy-image?url=${encodeURIComponent(originalUrl)}`;
            console.log("Utilisation du proxy pour charger l'image:", proxyUrl);
            
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = (e) => {
              console.error("Erreur lors du chargement de l'image via proxy:", e);
              reject(new Error(`Erreur de chargement de l'image: ${e}`));
            };
            
            img.src = proxyUrl;
          });
        };
        
        // Pour chaque produit avec ses codes-barres
        for (const product of products) {
          try {
            // Charger l'image du code-barres
            console.log(`Chargement de l'image pour ${product.name} depuis ${product.barcodeUrl}`);
            const img = await loadImage(product.barcodeUrl);
            
            // Ajouter l'image pour chaque quantité demandée
            for (let i = 0; i < product.quantity; i++) {
              if (x >= margin + (barcodeWidth + margin) * columns) {
                x = margin;
                y += barcodeHeight + margin;
              }
              
              if (y >= pageHeight - margin) {
                doc.addPage();
                pageIndex++;
                addPagination(pageIndex);
                x = margin;
                y = margin;
              }
              
              // Ajouter le nom du produit
              doc.setFontSize(8);
              doc.text(product.name, x + barcodeWidth / 2, y - 2, { align: "center" });
              
              // Ajouter l'image du code-barres
              doc.addImage(img, 'PNG', x, y, barcodeWidth, barcodeHeight - 5);
              
              x += barcodeWidth + margin;
            }
          } catch (error) {
            console.error(`Erreur pour ${product.name}:`, error);
            
            // En cas d'erreur, ajouter un texte à la place de l'image
            for (let i = 0; i < product.quantity; i++) {
              if (x >= margin + (barcodeWidth + margin) * columns) {
                x = margin;
                y += barcodeHeight + margin;
              }
              
              if (y >= pageHeight - margin) {
                doc.addPage();
                pageIndex++;
                addPagination(pageIndex);
                x = margin;
                y = margin;
              }
              
              doc.setFontSize(10);
              doc.text(`Erreur: ${product.name}`, x + barcodeWidth / 2, y + barcodeHeight / 2, { align: "center" });
              
              x += barcodeWidth + margin;
            }
          }
        }
        
        // Télécharger le PDF
        doc.save('codes-barres.pdf');
        resolve();
      } catch (error) {
        console.error("Erreur lors de la génération du PDF:", error);
        reject(error);
      }
    });
  };

  // Fonction pour générer le PDF optimisé pour Brother QL 700 (étiquettes 29x90mm)
  const generateBrotherQL700PDF = async (products) => {
    return new Promise(async (resolve, reject) => {
      try {
        // Dimensions de l'étiquette Brother QL 700 : 29x90mm
        const labelWidth = 90;  // mm
        const labelHeight = 29; // mm
        const margin = 2; // mm
        
        // Créer un PDF avec une page de format personnalisé
        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: [labelWidth, labelHeight]
        });

        // Supprimer la première page vide
        doc.deletePage(1);

        // Fonction pour charger une image depuis une URL en utilisant le proxy
        const loadImage = (originalUrl) => {
          return new Promise((resolve, reject) => {
            const proxyUrl = `${api.defaults.baseURL}/barcodes/proxy-image?url=${encodeURIComponent(originalUrl)}`;
            console.log("Chargement image Brother QL 700:", proxyUrl);
            
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = (e) => {
              console.error("Erreur lors du chargement de l'image via proxy:", e);
              reject(new Error(`Erreur de chargement de l'image: ${e}`));
            };
            
            img.src = proxyUrl;
          });
        };

        // Générer une étiquette pour chaque produit (selon la quantité)
        for (const product of products) {
          try {
            console.log(`Génération étiquette Brother QL 700 pour ${product.name}`);
            const img = await loadImage(product.barcodeUrl);
            
            // Créer une étiquette pour chaque quantité demandée
            for (let i = 0; i < product.quantity; i++) {
              // Ajouter une nouvelle page pour chaque étiquette
              doc.addPage([labelWidth, labelHeight], 'landscape');
              
              // Calculer les dimensions pour optimiser l'espace
              const availableWidth = labelWidth - (2 * margin);
              const availableHeight = labelHeight - (2 * margin);
              
              // Zone pour le texte du nom du produit (en haut) - ENCORE PLUS GROSSE
              const textHeight = 12; // mm (augmenté de 8 à 12)
              const textY = margin + 1;
              
              // Zone pour le code-barres (en bas) - ENCORE PLUS PETITE
              const barcodeHeight = availableHeight - textHeight - 1; // Réduit encore l'espacement
              const barcodeWidth = availableWidth * 0.5; // Réduit la largeur à 50%
              const barcodeX = margin + (availableWidth - barcodeWidth) / 2; // Centre le code-barres
              const barcodeY = textY + textHeight + 0.5;
              
              // Ajouter le nom du produit (tronqué si nécessaire) - ENCORE PLUS GROS
              doc.setFontSize(12); // Augmenté de 10 à 12
              doc.setFont('helvetica', 'bold');
              const maxTextWidth = availableWidth;
              let productName = product.name;
              
              // Tronquer le nom si nécessaire
              const textWidth = doc.getTextWidth(productName);
              if (textWidth > maxTextWidth) {
                while (doc.getTextWidth(productName + '...') > maxTextWidth && productName.length > 0) {
                  productName = productName.slice(0, -1);
                }
                productName += '...';
              }
              
              // Centrer le texte
              doc.text(productName, labelWidth / 2, textY + 6, { align: 'center' });
              
              // Ajouter le code-barres (centré et optimisé pour le scan) - SEULEMENT L'IMAGE
              doc.addImage(img, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
              
              // Optionnel : ajouter une bordure fine pour le découpage
              doc.setDrawColor(200, 200, 200);
              doc.setLineWidth(0.1);
              doc.rect(0.5, 0.5, labelWidth - 1, labelHeight - 1);
            }
          } catch (error) {
            console.error(`Erreur pour ${product.name}:`, error);
            
            // En cas d'erreur, créer une étiquette avec le nom seulement
            for (let i = 0; i < product.quantity; i++) {
              doc.addPage([labelWidth, labelHeight], 'landscape');
              
              doc.setFontSize(12); // Plus gros pour l'erreur
              doc.setFont('helvetica', 'bold');
              doc.text(`ERREUR: ${product.name}`, labelWidth / 2, labelHeight / 2, { align: 'center' });
              
              doc.setFontSize(8);
              doc.text('Code-barres non disponible', labelWidth / 2, labelHeight / 2 + 5, { align: 'center' });
              
              // Bordure d'erreur
              doc.setDrawColor(255, 0, 0);
              doc.setLineWidth(0.5);
              doc.rect(1, 1, labelWidth - 2, labelHeight - 2);
            }
          }
        }
        
        // Télécharger le PDF
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        doc.save(`etiquettes-brother-ql700-${timestamp}.pdf`);
        resolve();
      } catch (error) {
        console.error("Erreur lors de la génération du PDF Brother QL 700:", error);
        reject(error);
      }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <BOLayout>
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">
          Gestion des Codes-barres
      </h1>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="mb-8">
        {!productsLoaded ? (
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-4 disabled:opacity-50"
            onClick={loadProducts}
            disabled={loading}
          >
            Charger les produits
          </button>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                onClick={() => generateBarcodes(false)}
                disabled={loading}
              >
                {productsStats.withBarcode > 0 ? "Re-générer tous les codes-barres" : "Générer les codes-barres"}
              </button>
              
              {productsStats.withoutBarcode > 0 && (
                <button
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                  onClick={() => generateBarcodes(true)}
                  disabled={loading}
                >
                  Générer les codes-barres manquants ({productsStats.withoutBarcode})
                </button>
              )}
            </div>
            
            <div className="bg-gray-100 p-4 rounded-md">
              <h2 className="text-lg font-semibold mb-2">Filtres et Statistiques</h2>
              
              {/* Filtre par catégorie */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filtrer par catégorie:
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setPage(1); // Remettre à la première page lors du changement de filtre
                  }}
                  className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full max-w-sm"
                  disabled={loadingCategories}
                >
                  <option value="">Toutes les catégories</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Statistiques */}
              <div className="text-sm">
                <p>Total de produits {selectedCategory ? 'dans cette catégorie' : ''} : {productsStats.total}</p>
              <p>Produits avec code-barres : {productsStats.withBarcode}</p>
              <p>Produits sans code-barres : {productsStats.withoutBarcode}</p>
              </div>
            </div>
            
            <div className="mt-6">
              <h2 className="text-lg font-semibold mb-2">Impression des étiquettes</h2>
              <div className="flex flex-wrap items-center gap-4 mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantité par étiquette:
                  </label>
                  <input
                    type="number"
                    className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-24"
                    value={selectedQuantity}
                    onChange={(e) => setSelectedQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Format d'impression:
                  </label>
                  <select
                    value={printFormat}
                    onChange={(e) => setPrintFormat(e.target.value)}
                    className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  >
                    <option value="brother-ql700">Brother QL 700 (29x90mm)</option>
                    <option value="standard">Standard (A4)</option>
                  </select>
                </div>
                
                <button
                  className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                  onClick={printBarcodes}
                  disabled={generatingPDF || Object.keys(selectedProducts).length === 0}
                >
                  {generatingPDF ? 'Génération du PDF...' : 'Imprimer les étiquettes sélectionnées'}
                </button>
                
                <div className="flex gap-2">
                  <button 
                    className="bg-gray-500 hover:bg-gray-700 text-white py-1 px-2 rounded text-sm"
                    onClick={selectAllProducts}
                  >
                    Tout sélectionner
                  </button>
                  <button 
                    className="bg-gray-500 hover:bg-gray-700 text-white py-1 px-2 rounded text-sm"
                    onClick={deselectAllProducts}
                  >
                    Tout désélectionner
                  </button>
                  {!showAllBarcodes ? (
                    <button 
                      className="bg-gray-500 hover:bg-gray-700 text-white py-1 px-2 rounded text-sm"
                      onClick={loadAllBarcodes}
                    >
                      Tout afficher
                    </button>
                  ) : (
                    <button 
                      className="bg-gray-500 hover:bg-gray-700 text-white py-1 px-2 rounded text-sm"
                      onClick={hideAllBarcodes}
                    >
                      Tout masquer
                    </button>
                  )}
                </div>
              </div>
              
              {/* Information sur les formats */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-4">
                <h3 className="text-sm font-medium text-blue-800 mb-2">Formats d'impression disponibles :</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li><strong>Brother QL 700 (29x90mm)</strong> : Format optimisé pour les étiquettes autocollantes Brother QL 700. Chaque étiquette fait 29x90mm avec le nom du produit et le code-barres.</li>
                  <li><strong>Standard (A4)</strong> : Format classique sur papier A4 avec plusieurs codes-barres par page.</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {productsLoaded && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => (
              <div 
                key={product.productId} 
                className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                  selectedProducts[product.productId] ? 'border-blue-500' : 'border-transparent'
                } hover:shadow-lg transition-all`}
              >
                <div className="p-4">
                  <div className="flex justify-between">
                    <h2 className="text-xl font-semibold mb-2">
                      {product.name}
                    </h2>
                    {product.hasBarcode && (
                      <div className="w-6 h-6">
                        <input 
                          type="checkbox" 
                          checked={!!selectedProducts[product.productId]} 
                          onChange={() => toggleProductSelection(product.productId)}
                          className="w-5 h-5"
                        />
                      </div>
                    )}
                  </div>
                  
                  {product.category && (
                    <p className="text-gray-600 mb-2">
                      Catégorie: {product.category}
                    </p>
                  )}

                  {product.hasBarcode ? (
                    <div className="mt-2">
                      {(expandedProduct === product.productId || showAllBarcodes) && product.barcodeUrl ? (
                        <img
                          src={product.barcodeUrl}
                          alt={`Code-barres pour ${product.name}`}
                          className="w-full max-w-[300px] mx-auto"
                          loading="lazy"
                        />
                      ) : (
                        <button 
                          onClick={() => loadBarcodeImage(product.productId)}
                          className="w-full py-2 px-4 bg-gray-200 hover:bg-gray-300 rounded text-center"
                        >
                          {expandedProduct === product.productId ? "Masquer le code-barres" : "Afficher le code-barres"}
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="text-red-600">
                      Pas de code-barres généré
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-6">
            <nav className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p-1))}
                disabled={page === 1}
                className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
              >
                Précédent
              </button>
              <span className="px-4 py-1">
                Page {page} sur {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p+1))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
              >
                Suivant
              </button>
              <select 
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="ml-4 px-2 py-1 border rounded"
              >
                <option value="12">12 par page</option>
                <option value="24">24 par page</option>
                <option value="48">48 par page</option>
              </select>
            </nav>
          </div>
        </>
      )}
      </div>
    </BOLayout>
  );
};

export default BOBarcodes; 